// @ts-strict

import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { ConfigurationSelectionComponent } from '../configuration-selection/configuration-selection.component';
import { DeeplinkConfigService } from '@app/core/services/deeplink-config/deeplink-config.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { UrlAddressConfigService } from '@services/url-address-config/url-address-config.service';
import { TextInputComponent } from '@app/shared/components/modal-inputs/text-input/text-input.component';
import { ISingleCampaign, ICampaignConfigFieldData, SpecialCampaignData } from '@app/core/models/core-models';
import { copyToClipboard } from '@app/core/models/helpers';
import { ProgramConfiguration } from '@services/url-address-config/url-address-config.types';
import { SelectContainerChosenValue } from '@app/shared/components/select-container/select-container.component';
import { HttpErrorResponse } from '@angular/common/http';
import { IDeeplinkConfigData } from '@app/core/services/deeplink-config/deeplink-config.service.types';
import { GlobalNotificationsService } from '@app/core/services/global-notifications/global-notifications.service';
import { NotificationTypes } from '@app/core/services/global-notifications/global-notifications.model';
import { ISelectOption } from '@app/shared/types/inputs';

@Component({
  selector: 'app-deep-link-modal',
  templateUrl: './deep-link-modal.component.html',
  styleUrls: ['./deep-link-modal.component.scss']
})
export class DeepLinkModalComponent {
  @Input() currentCampaign?: ISingleCampaign | SpecialCampaignData;
  @ViewChild('backButtonRef') backButtonRef?: ElementRef<HTMLDivElement>;
  @ViewChild(ConfigurationSelectionComponent) configurationSelection?: ConfigurationSelectionComponent;
  @ViewChild(TextInputComponent) urlAddressComponent?: TextInputComponent;

  public submitted = false;
  public isSuccessfullySubmitted = false;
  public selectedConfiguration?: number;
  public selectedConfigurationName?: string;
  public generatedDeeplink?: string;
  public isLoaded = false;
  public wrongURL = false;
  public landings: ISelectOption[] = [];
  public selectedLandingPage?: number;
  public selectData: {
    title: string;
    data: {
      id: number;
      name: string;
    }[];
  } = {
    title: 'tracking_domain',
    data: []
  };
  public idOfDefaultValue = 0;
  public modalLoading = true;
  public field = {
    system_name: 'deeplink_url'
  };

  private urlAddress?: string;
  private trackingDomains: ICampaignConfigFieldData = { system_name: 'domain', values: [] };
  private configName: string = '';
  private selectedDomain = 0;

  constructor(
    private globalNotificationsService: GlobalNotificationsService,
    private deeplinkService: DeeplinkConfigService,
    public activeModal: NgbActiveModal,
    private configService: UrlAddressConfigService
  ) {}

  public getPickedConfig(element: ProgramConfiguration): void {
    this.configName = element.name || '';
    this.getProgramDomains(element.id || 0);
    this.getProgramLandings(element.id || 0);
  }

  public getSelectedValue(value: SelectContainerChosenValue): void {
    this.selectedDomain = value.choosenValue.id;
    this.idOfDefaultValue = ((this.trackingDomains.values as Record<string, string | number>[]) || [])?.findIndex(
      domain => domain.id === value.choosenValue.id
    );
  }

  public actionButton(): void {
    if (this.isSuccessfullySubmitted) {
      this.activeModal.close();
    } else {
      this.verify();
    }
  }

  public async copyInputMessage(text: string): Promise<void> {
    const { message, success } = await copyToClipboard(text, { successMessage: 'view_translations.link_copied' });

    this.showNotification(message, NotificationTypes[success ? 3 : 1], true);
  }

  public getLanding(event: any): void {
    if (event.id) {
      this.selectedLandingPage = event.id;
    } else {
      this.selectedLandingPage = undefined;
    }
  }

  private getProgramDomains(configID: number): void {
    this.modalLoading = true;
    this.configService.getProgramDomains(this.currentCampaign?.id || 0, configID, 'deeplink').subscribe(res => {
      this.trackingDomains.values = (res.data?.domains || []).map(domain => ({ ...domain, value: domain.domain || '' }));
      this.selectData.data = [];
      this.trackingDomains.system_name = 'domain';

      this.trackingDomains.values.map(trackingDomain => {
        this.selectData.data.push({
          id: (trackingDomain.id as number) || 0,
          name: (trackingDomain.domain as string) || ''
        });
      });

      const firstItem = this.trackingDomains.values?.[0]?.id;

      this.selectedDomain = typeof firstItem === 'string' ? Number(firstItem) : typeof firstItem === 'number' ? firstItem : 0;
      this.modalLoading = false;
    });
  }

  private getProgramLandings(configID: number): void {
    this.configService.getEditConfigData(this.currentCampaign?.id || 0, configID).subscribe(res => {
      if (res.data?.landings && res.data?.landings.length > 0) {
        const tempArray: ISelectOption[] = [];
        res.data.landings.forEach(item => {
          tempArray.push({ id: item.id, value: item.id, name: item.name });
        });
        this.landings = tempArray;
      }
    });
  }

  private verify(): void {
    this.submitted = true;
    this.wrongURL = false;
    this.selectedConfiguration = this.configurationSelection?.configurationSelect.value.selectedConfiguration;
    this.urlAddress = this.urlAddressComponent?.textForm.value.text;
    if (!this.selectedConfiguration || !this.urlAddress) {
      this.showNotification('check_if_fields_are_filled_correctly', NotificationTypes[1]);
      return;
    }
    this.deeplinkService.addDeeplinkConfiguration(this.selectedConfiguration, this.urlAddress, this.currentCampaign?.id || 0, this.selectedDomain).subscribe(
      res => {
        this.showNotification('you_deeplink_configuration_is_successfully_saved', NotificationTypes[0]);
        this.isSuccessfullySubmitted = true;
        this.generatedDeeplink = res.data?.deeplink || '';
        const deeplinkWithoutLd = this.generatedDeeplink; // it will be visible on deeplinks list, we dont want landing id there
        if (this.selectedLandingPage) {
          this.generatedDeeplink = this.generatedDeeplink + '&ld=' + this.selectedLandingPage;
        }
        const newDeeplinkData: IDeeplinkConfigData = {
          program_config_name: this.configName,
          program_config_id: this.selectedConfiguration,
          created_at: new Date().toISOString(),
          source_url: this.urlAddress,
          deeplink: deeplinkWithoutLd,
          updated_at: new Date().toISOString()
        };
        this.selectedConfigurationName = this.configName;
        this.isLoaded = true;
        this.deeplinkService.newDeeplink.next(newDeeplinkData);

        if (this.backButtonRef?.nativeElement) {
          this.backButtonRef.nativeElement.scrollIntoView({
            behavior: 'auto'
          });
        }
      },
      (err: unknown) => {
        if (err instanceof HttpErrorResponse) {
          if (err.status === 422) {
            this.showNotification('wrong_domain_deeplink', NotificationTypes[1]);
            this.wrongURL = true;
            return;
          }
        }

        this.showNotification('something_went_wrong_try_again_later', NotificationTypes[1]);
      }
    );
  }

  private showNotification(message: string, type: (typeof NotificationTypes)[number], translate: boolean = true): void {
    this.globalNotificationsService.show({
      message: translate && !message.includes('view_translations.') ? `view_translations.${message}` : message,
      type,
      translate,
      isPersistent: false,
      id: ''
    });
  }
}
